import { useCallback, useState } from 'react';

export type ReportsLayoutMode = 'tabs' | 'document';

export function isReportsLayoutMode(
  value: unknown
): value is ReportsLayoutMode {
  return value === 'tabs' || value === 'document';
}

const STORAGE_KEY = 'reports.layoutMode';

/**
 * Anything unrecognised — including the retired `sitstat` value a browser may
 * still have stored — falls back to Document.
 */
export function resolveStoredReportsLayoutMode(
  stored: string | null
): ReportsLayoutMode {
  if (stored === 'tabs' || stored === 'document') {
    return stored;
  }
  return 'document';
}

const readStoredMode = (): ReportsLayoutMode => {
  try {
    return resolveStoredReportsLayoutMode(
      window.localStorage.getItem(STORAGE_KEY)
    );
  } catch {
    return 'document';
  }
};

/**
 * Tabs = section tabs panel; Document = continuous doc + outline. Unset reports
 * default to Document. Persisted per browser.
 */
export const useReportsLayoutMode = () => {
  const [layoutMode, setLayoutModeState] =
    useState<ReportsLayoutMode>(readStoredMode);

  const setLayoutMode = useCallback((mode: ReportsLayoutMode) => {
    setLayoutModeState(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Storage unavailable — mode still works in-memory.
    }
  }, []);

  return { layoutMode, setLayoutMode };
};
